import { Authenticator } from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import clsx from "clsx";
import { FlagsProvider } from "flagged";
import { ReactElement } from "react";
import { Slide, ToastContainer } from "react-toastify";

import ErrorBoundary from "./components/ErrorBoundary.tsx";
import MonacoSetupProvider from "./components/json/MonacoSetupProvider.tsx";
import { getAppConfig } from "./config/get-app-config.ts";
import { flags } from "./featureflags.ts";
import { Routes } from "./pages/Routes.tsx";
import { AUTH_CALLBACK_PATH } from "./stores/router.ts";

const AUTH_SCOPE = ["email openid phone"];
const REDIRECT_CALLBACK = `${window.location.origin}${AUTH_CALLBACK_PATH}`;
const appConfig = getAppConfig();

interface ToastContext {
  type?: "error" | "info" | "success" | "warning";
}

const toastTypeStyles: Record<NonNullable<ToastContext["type"]>, string> = {
  error: "bg-red-950 text-red-500",
  info: "bg-sky-950 text-sky-400",
  success: "bg-emerald-950 text-emerald-500",
  warning: "bg-yellow-100 text-yellow-800",
};

const baseToastClass =
  "flex items-center rounded-lg p-4 text-sm shadow-md min-w-[300px]";

Amplify.configure({
  Auth: {
    Cognito: {
      loginWith: {
        oauth: {
          domain: appConfig.cognitoDomainName,
          redirectSignIn: [REDIRECT_CALLBACK],
          redirectSignOut: [],
          responseType: "code",
          scopes: AUTH_SCOPE,
        },
      },
      userPoolClientId: appConfig.cognitoClientId,
      userPoolId: appConfig.cognitoUserPoolId,
    },
  },
});

export const App = (): ReactElement => {
  return (
    <ErrorBoundary>
      <Authenticator.Provider>
        <FlagsProvider features={flags}>
          <MonacoSetupProvider />

          <Routes />

          <ToastContainer
            autoClose={5000}
            className="flex-1 text-left"
            closeOnClick
            draggable={false}
            hideProgressBar
            newestOnTop={false}
            pauseOnFocusLoss
            pauseOnHover
            position="bottom-center"
            rtl={false}
            theme="colored"
            toastClassName={(context) => {
              const type = (context as ToastContext).type;
              const style =
                type && toastTypeStyles[type]
                  ? toastTypeStyles[type]
                  : "bg-gray-800 text-white";
              return clsx(baseToastClass, style);
            }}
            transition={Slide}
          />
        </FlagsProvider>
      </Authenticator.Provider>
    </ErrorBoundary>
  );
};
